import { isDiagnostic, isInProgress, isUnfinished } from "@helpers/exams";
import {
	convertToMinutes,
	formatSecondsToUTCtime,
} from "@memorang/helpers/common";
import {
	type SkillIdentity,
	examCoversSkill,
} from "@memorang/helpers/language-learning";
import type { PracticeTestExam } from "@memorang/types";
import type { TFunction } from "i18next";
import {
	PRACTICE_TEST_COOLDOWN_DAYS,
	SECTIONAL_TEST_COOLDOWN_DAYS,
	getNextEligibleTest,
	getRecentActiveTest,
} from "../hooks/useRecommendedTasksSection/helpers";
import {
	filterFullPracticeTests,
	filterSectionalTests,
} from "../hooks/useRecommendedTasksSection/test-kinds";

const SECONDS_PER_HOUR = 3600;

type RecommendedTestKind = "diagnostic" | "sectional" | "fullMock";

type RecommendedTest = {
	kind: RecommendedTestKind;
	exam: PracticeTestExam;
};

const preferInProgress = (exams: PracticeTestExam[]) =>
	exams.find(isInProgress) ?? exams[0];

export const selectRecommendedTestsForSkill = ({
	exams,
	skill,
}: {
	exams: PracticeTestExam[] | undefined;
	skill: SkillIdentity;
}): RecommendedTest[] => {
	const unfinished = (exams ?? []).filter(isUnfinished);
	const skillScoped = unfinished.filter((exam) => examCoversSkill(exam, skill));
	const skillScopedPracticeTests = skillScoped.filter(
		(exam) => !isDiagnostic(exam),
	);
	const practiceTests = unfinished.filter((exam) => !isDiagnostic(exam));

	const byKind: Record<RecommendedTestKind, PracticeTestExam | undefined> = {
		diagnostic: preferInProgress(skillScoped.filter(isDiagnostic)),
		sectional: preferInProgress(filterSectionalTests(skillScopedPracticeTests)),
		fullMock: preferInProgress(filterFullPracticeTests(practiceTests)),
	};

	return (["diagnostic", "sectional", "fullMock"] as const).flatMap((kind) => {
		const exam = byKind[kind];
		return exam ? [{ kind, exam }] : [];
	});
};

export type SkillTestRecommendation = {
	kind: "resume" | RecommendedTestKind;
	exam: PracticeTestExam;
};

/** Resume wins if a covering test is in progress; otherwise diagnostic, then sectional, then full mock. */
export const selectKnownLevelTestsForSkill = ({
	exams,
	skill,
}: {
	exams: PracticeTestExam[] | undefined;
	skill: SkillIdentity;
}): SkillTestRecommendation[] => {
	const covering = (exams ?? []).filter((exam) => examCoversSkill(exam, skill));
	const resume = getRecentActiveTest(covering);
	const excludingResume = (list: PracticeTestExam[]) =>
		list.filter((exam) => exam.id !== resume?.id);

	// Diagnostics have no cooldown, so just check they're unfinished.
	const diagnostics = excludingResume(covering).filter(
		(exam) => isDiagnostic(exam) && isUnfinished(exam),
	);
	const diagnostic = diagnostics[0];

	// COMPLETE exams stay in the pool: getNextEligibleTest needs the most
	// recent COMPLETE entry to compute the cooldown.
	const skillScopedPracticeTests = excludingResume(covering).filter(
		(exam) => !isDiagnostic(exam),
	);
	// Full mocks rarely name or tag the skill, so this pulls from all exams
	// instead of the skill-scoped list, same as selectRecommendedTestsForSkill.
	const practiceTests = excludingResume(exams ?? []).filter(
		(exam) => !isDiagnostic(exam),
	);
	const sectional = getNextEligibleTest({
		tests: filterSectionalTests(skillScopedPracticeTests),
		cooldownDays: SECTIONAL_TEST_COOLDOWN_DAYS,
	});
	const fullMock = getNextEligibleTest({
		tests: filterFullPracticeTests(practiceTests),
		cooldownDays: PRACTICE_TEST_COOLDOWN_DAYS,
	});

	return [
		...(resume ? [{ kind: "resume" as const, exam: resume }] : []),
		...(diagnostic ? [{ kind: "diagnostic" as const, exam: diagnostic }] : []),
		...(sectional ? [{ kind: "sectional" as const, exam: sectional }] : []),
		...(fullMock ? [{ kind: "fullMock" as const, exam: fullMock }] : []),
	];
};

export const formatDurationLabel = (totalTime: number, t: TFunction): string =>
	totalTime >= SECONDS_PER_HOUR
		? formatSecondsToUTCtime(totalTime)
		: t("sessionConfig.quickStudy.duration", {
				minutes: convertToMinutes(totalTime),
			});

export const GUIDANCE_KEY: Record<RecommendedTestKind, string> = {
	diagnostic: "progress.languageLearning.startFirstTest.guidance.diagnostic",
	sectional: "progress.languageLearning.startFirstTest.guidance.sectional",
	fullMock: "progress.languageLearning.startFirstTest.guidance.fullMock",
};
